import { create } from 'zustand';
import { nanoid } from 'nanoid';
import { api } from '../lib/api-client';
import type { Annotation, IkPoseEntry, PhysicsConstraintPose, Layer, LayerBlendMode, LayerGroup, Transform, AnimationTrack, SceneData } from '@spine-workspace/shared';
import { UNDO_STACK_SIZE } from '@spine-workspace/shared';
import { composeTransforms, worldToLocal } from '../lib/transform-utils';

// ---- RAF-throttled transform batching ----------------------------------------
// Accumulates transform deltas from rapid drag events and flushes them in a
// single store update per animation frame, reducing React re-renders during drag.
let _transformRafId: number | null = null;
const _pendingTransforms = new Map<string, Partial<Transform>>();
type SetFn = (fn: (s: SceneState) => Partial<SceneState>) => void;
let _storeSet: SetFn | null = null;

function _flushTransforms() {
  _transformRafId = null;
  if (_pendingTransforms.size === 0 || !_storeSet) return;
  const snapshot = new Map(_pendingTransforms);
  _pendingTransforms.clear();
  _storeSet((s) => ({
    layers: s.layers.map((l) => {
      const t = snapshot.get(l.id);
      return t ? { ...l, transform: { ...l.transform, ...t } } : l;
    }),
    isDirty: true,
  }));
}
// ------------------------------------------------------------------------------

type LayerSnapshot = Layer[];

interface SceneState {
  sceneId: string | null;
  sceneName: string;
  canvasWidth: number;
  canvasHeight: number;
  backgroundColor: string | null;
  layers: Layer[];
  layerGroups: LayerGroup[];
  selectedLayerId: string | null;
  selectedLayerIds: string[];
  annotations: Annotation[];

  // Actions
  initScene: (id: string, name: string, w: number, h: number, layers: Layer[], backgroundColor?: string | null, layerGroups?: LayerGroup[], notes?: string) => void;
  setBackgroundColor: (color: string | null) => void;
  addLayer: (assetId: string, assetName: string, animations: string[], x: number, y: number) => Layer;
  addImageLayer: (assetId: string, name: string, imageUrl: string, x: number, y: number) => Layer;
  removeLayer: (layerId: string) => void;
  updateLayerTransform: (layerId: string, transform: Partial<Transform>) => void;
  setLayerAnimation: (layerId: string, trackIndex: number, animName: string, loop: boolean) => void;
  addAnimationTrack: (layerId: string, animName: string) => void;
  setLayerAnimationTracks: (layerId: string, tracks: AnimationTrack[]) => void;
  removeAnimationTrack: (layerId: string, trackIndex: number) => void;
  setLayerSkin: (layerId: string, skinName: string) => void;
  setLayerOpacity: (layerId: string, opacity: number) => void;
  setLayerBlendMode: (layerId: string, mode: LayerBlendMode) => void;
  setLayerEffects: (layerId: string, effects: Partial<import('@spine-workspace/shared').LayerEffects>) => void;
  reorderLayers: (fromIndex: number, toIndex: number) => void;
  toggleLayerVisibility: (layerId: string) => void;
  soloLayer: (layerId: string) => void;
  soloedLayerId: string | null;
  toggleLayerLock: (layerId: string) => void;
  setLayerAttachment: (layerId: string, parentLayerId: string | null, boneName: string | null) => void;
  selectLayer: (layerId: string | null, opts?: { multi?: boolean; range?: boolean }) => void;
  selectLayers: (layerIds: string[]) => void;
  clearMultiSelection: () => void;
  renameLayer: (layerId: string, name: string) => void;
  duplicateLayer: (layerId: string) => void;
  setLayerColor: (layerId: string, color: string | null) => void;
  moveLayerUp: (layerId: string) => void;
  moveLayerDown: (layerId: string) => void;
  updateImageLayer: (layerId: string, assetId: string, imageUrl: string) => void;
  setLayerIkPose: (layerId: string, constraintName: string, pose: IkPoseEntry) => void;
  setLayerPhysicsPose: (layerId: string, constraintName: string, pose: PhysicsConstraintPose) => void;

  // Layer groups
  selectedGroupId: string | null;
  selectGroup: (groupId: string | null) => void;
  selectGroupLayers: (groupId: string) => void;
  addLayerGroup: () => LayerGroup;
  removeLayerGroup: (groupId: string) => void;
  renameLayerGroup: (groupId: string, name: string) => void;
  toggleGroupCollapsed: (groupId: string) => void;
  setLayerGroup: (layerId: string, groupId: string | null) => void;
  reorderGroups: (fromId: string, toId: string) => void;

  // Annotations
  fetchAnnotations: () => Promise<void>;
  addAnnotation: (x: number, y: number, text: string) => Promise<void>;
  updateAnnotation: (id: string, text: string) => Promise<void>;
  resolveAnnotation: (id: string, resolved: boolean) => void;
  deleteAnnotation: (id: string) => Promise<void>;

  // Undo / Redo
  undoStack: LayerSnapshot[];
  redoStack: LayerSnapshot[];
  undoLabels: string[];
  pushUndo: (label?: string) => void;
  undo: () => void;
  redo: () => void;
  undoTo: (index: number) => void;

  // Persistence
  isDirty: boolean;
  lastSavedVersion: number;
  save: () => Promise<void>;

  setTrackTimeScale: (layerId: string, trackIndex: number, timeScale: number) => void;

  // Canvas size
  setCanvasSize: (w: number, h: number) => void;
  setSceneName: (name: string) => void;

  // Scene notes
  sceneNotes: string;
  setSceneNotes: (notes: string) => void;

  // Computed
  selectedLayer: () => Layer | null;
  toSceneData: () => SceneData;
}

export const useSceneStore = create<SceneState>((set, get) => {
  // Wire the module-level flush function to this store's set.
  _storeSet = set as SetFn;
  return ({
  sceneId: null,
  sceneName: '',
  canvasWidth: 1920,
  canvasHeight: 1080,
  backgroundColor: null,
  layers: [],
  layerGroups: [],
  selectedLayerId: null,
  selectedLayerIds: [],
  selectedGroupId: null,
  soloedLayerId: null,
  annotations: [],
  undoStack: [],
  redoStack: [],
  undoLabels: [],
  sceneNotes: '',
  isDirty: false,
  lastSavedVersion: 1,

  initScene: (id, name, w, h, layers, backgroundColor = null, layerGroups = [], notes = '') =>
    set({ sceneId: id, sceneName: name, canvasWidth: w, canvasHeight: h, layers, layerGroups, backgroundColor, sceneNotes: notes, isDirty: false }),

  setBackgroundColor: (color) => set({ backgroundColor: color, isDirty: true }),

  addLayer: (assetId, assetName, animations, x, y) => {
    get().pushUndo();
    const newLayer: Layer = {
      id: nanoid(12),
      assetId,
      name: assetName,
      visible: true,
      locked: false,
      zIndex: get().layers.length,
      transform: { x, y, scaleX: 1, scaleY: 1, rotation: 0 },
      animation: {
        tracks: animations[0]
          ? [{ trackIndex: 0, name: animations[0], loop: true, timeScale: 1 }]
          : [],
        paused: false,
      },
      skin: 'default',
      attachment: { parentLayerId: null, boneName: null },
      opacity: 1,
      layerType: 'spine',
    };
    set((s) => ({ layers: [...s.layers, newLayer], isDirty: true }));
    return newLayer;
  },

  addImageLayer: (assetId, name, imageUrl, x, y) => {
    get().pushUndo();
    const newLayer: Layer = {
      id: nanoid(12),
      assetId,
      name,
      visible: true,
      locked: false,
      zIndex: get().layers.length,
      transform: { x, y, scaleX: 1, scaleY: 1, rotation: 0 },
      animation: { tracks: [], paused: false },
      skin: 'default',
      attachment: { parentLayerId: null, boneName: null },
      opacity: 1,
      layerType: 'image',
      imageUrl,
    };
    set((s) => ({ layers: [...s.layers, newLayer], isDirty: true }));
    return newLayer;
  },

  removeLayer: (layerId) => {
    get().pushUndo();
    set((s) => {
      const removed = s.layers.find((l) => l.id === layerId);
      return {
        layers: s.layers
          .filter((l) => l.id !== layerId)
          .map((l) => {
            // Detach orphaned children: convert their local transform back to world space
            if (l.attachment.parentLayerId === layerId && removed) {
              return {
                ...l,
                transform: composeTransforms(l.transform, removed.transform),
                attachment: { parentLayerId: null, boneName: null },
              };
            }
            return l;
          }),
        selectedLayerId: s.selectedLayerId === layerId ? null : s.selectedLayerId,
        isDirty: true,
      };
    });
  },

  updateLayerTransform: (layerId, transform) => {
    // Merge into pending batch (latest value wins per key).
    const existing = _pendingTransforms.get(layerId);
    _pendingTransforms.set(layerId, { ...(existing ?? {}), ...transform });

    // Schedule a single flush per animation frame so rapid drag events produce
    // only one React re-render per frame instead of one per mouse-move pixel.
    if (_transformRafId === null) {
      _transformRafId = requestAnimationFrame(_flushTransforms);
    }
  },

  setLayerAnimation: (layerId, trackIndex, animName, loop) => {
    set((s) => ({
      layers: s.layers.map((l) =>
        l.id !== layerId
          ? l
          : {
              ...l,
              animation: {
                ...l.animation,
                tracks: l.animation.tracks.map((t) =>
                  t.trackIndex === trackIndex ? { ...t, name: animName, loop } : t
                ),
              },
            }
      ),
      isDirty: true,
    }));
  },

  addAnimationTrack: (layerId, animName) => {
    set((s) => ({
      layers: s.layers.map((l) => {
        if (l.id !== layerId) return l;
        const nextIndex = l.animation.tracks.length;
        const newTrack: AnimationTrack = { trackIndex: nextIndex, name: animName, loop: true, timeScale: 1 };
        return { ...l, animation: { ...l.animation, tracks: [...l.animation.tracks, newTrack] } };
      }),
      isDirty: true,
    }));
  },

  setLayerAnimationTracks: (layerId, tracks) => {
    set((s) => ({
      layers: s.layers.map((l) =>
        l.id === layerId ? { ...l, animation: { ...l.animation, tracks } } : l
      ),
      isDirty: true,
    }));
  },

  removeAnimationTrack: (layerId, trackIndex) => {
    set((s) => ({
      layers: s.layers.map((l) =>
        l.id !== layerId
          ? l
          : {
              ...l,
              animation: {
                ...l.animation,
                tracks: l.animation.tracks
                  .filter((t) => t.trackIndex !== trackIndex)
                  .map((t, i) => ({ ...t, trackIndex: i })),
              },
            }
      ),
      isDirty: true,
    }));
  },

  setLayerSkin: (layerId, skinName) => {
    set((s) => ({
      layers: s.layers.map((l) => (l.id === layerId ? { ...l, skin: skinName } : l)),
      isDirty: true,
    }));
  },

  setLayerOpacity: (layerId, opacity) => {
    set((s) => ({
      layers: s.layers.map((l) => (l.id === layerId ? { ...l, opacity } : l)),
      isDirty: true,
    }));
  },

  setLayerBlendMode: (layerId, mode) => {
    set((s) => ({
      layers: s.layers.map((l) => (l.id === layerId ? { ...l, blendMode: mode } : l)),
      isDirty: true,
    }));
  },

  setLayerEffects: (layerId, effects) => {
    set((s) => ({
      layers: s.layers.map((l) =>
        l.id === layerId
          ? { ...l, effects: { brightness: 1, saturation: 0, tint: null, ...l.effects, ...effects } }
          : l
      ),
      isDirty: true,
    }));
  },

  reorderLayers: (fromIndex, toIndex) => {
    get().pushUndo();
    set((s) => {
      const layers = [...s.layers];
      const [moved] = layers.splice(fromIndex, 1);
      layers.splice(toIndex, 0, moved);
      return { layers: layers.map((l, i) => ({ ...l, zIndex: i })), isDirty: true };
    });
  },

  moveLayerUp: (layerId) => {
    // Layers array: index 0 = bottom, index n-1 = top (LayerList renders reversed).
    // "Up" means higher z-order → higher array index.
    set((s) => {
      const idx = s.layers.findIndex((l) => l.id === layerId);
      if (idx < 0 || idx >= s.layers.length - 1) return s;
      const layers = [...s.layers];
      [layers[idx], layers[idx + 1]] = [layers[idx + 1], layers[idx]];
      return { layers: layers.map((l, i) => ({ ...l, zIndex: i })), isDirty: true };
    });
  },

  moveLayerDown: (layerId) => {
    // "Down" means lower z-order → lower array index.
    set((s) => {
      const idx = s.layers.findIndex((l) => l.id === layerId);
      if (idx <= 0) return s;
      const layers = [...s.layers];
      [layers[idx - 1], layers[idx]] = [layers[idx], layers[idx - 1]];
      return { layers: layers.map((l, i) => ({ ...l, zIndex: i })), isDirty: true };
    });
  },

  updateImageLayer: (layerId, assetId, imageUrl) => {
    set((s) => ({
      layers: s.layers.map((l) => l.id === layerId ? { ...l, assetId, imageUrl } : l),
      isDirty: true,
    }));
  },

  setLayerIkPose: (layerId, constraintName, pose) => {
    set((s) => ({
      layers: s.layers.map((l) =>
        l.id === layerId
          ? { ...l, ikPose: { ...l.ikPose, [constraintName]: pose } }
          : l
      ),
      isDirty: true,
    }));
  },

  setLayerPhysicsPose: (layerId, constraintName, pose) => {
    set((s) => ({
      layers: s.layers.map((l) =>
        l.id === layerId
          ? { ...l, physicsConstraintPose: { ...l.physicsConstraintPose, [constraintName]: pose } }
          : l
      ),
      isDirty: true,
    }));
  },

  toggleLayerVisibility: (layerId) => {
    set((s) => ({
      layers: s.layers.map((l) => (l.id === layerId ? { ...l, visible: !l.visible } : l)),
      isDirty: true,
    }));
  },

  soloLayer: (layerId) => {
    const { soloedLayerId, layers } = get();
    if (soloedLayerId === layerId) {
      // Un-solo: restore all layers to visible
      set({ soloedLayerId: null, layers: layers.map((l) => ({ ...l, visible: true })), isDirty: true });
    } else {
      // Solo this layer: hide all others
      set({
        soloedLayerId: layerId,
        layers: layers.map((l) => ({ ...l, visible: l.id === layerId })),
        isDirty: true,
      });
    }
  },

  toggleLayerLock: (layerId) => {
    set((s) => ({
      layers: s.layers.map((l) => (l.id === layerId ? { ...l, locked: !l.locked } : l)),
      isDirty: true,
    }));
  },

  setLayerAttachment: (layerId, parentLayerId, boneName) => {
    set((s) => {
      const child = s.layers.find((l) => l.id === layerId);
      if (!child) return { layers: s.layers, isDirty: true };

      const prevParentId = child.attachment.parentLayerId;
      let newTransform = child.transform;

      if (parentLayerId && !prevParentId) {
        // Fresh attach: convert child world → local
        const parent = s.layers.find((l) => l.id === parentLayerId);
        if (parent) newTransform = worldToLocal(child.transform, parent.transform);
      } else if (!parentLayerId && prevParentId) {
        // Detach: convert child local → world
        const prevParent = s.layers.find((l) => l.id === prevParentId);
        if (prevParent) newTransform = composeTransforms(child.transform, prevParent.transform);
      } else if (parentLayerId && prevParentId && parentLayerId !== prevParentId) {
        // Re-parent: local → world → local (relative to new parent)
        const prevParent = s.layers.find((l) => l.id === prevParentId);
        const newParent  = s.layers.find((l) => l.id === parentLayerId);
        if (prevParent && newParent) {
          const worldT = composeTransforms(child.transform, prevParent.transform);
          newTransform = worldToLocal(worldT, newParent.transform);
        }
      }
      // If same parent and only boneName changed — no coordinate conversion needed

      return {
        layers: s.layers.map((l) =>
          l.id === layerId
            ? { ...l, transform: newTransform, attachment: { parentLayerId, boneName } }
            : l
        ),
        isDirty: true,
      };
    });
  },

  selectLayer: (layerId, opts = {}) => {
    if (!layerId) {
      set({ selectedLayerId: null, selectedLayerIds: [], selectedGroupId: null });
      return;
    }
    const { multi, range } = opts;
    if (multi) {
      set((s) => {
        const ids = s.selectedLayerIds.includes(layerId)
          ? s.selectedLayerIds.filter((id) => id !== layerId)
          : [...s.selectedLayerIds, layerId];
        return { selectedLayerId: layerId, selectedLayerIds: ids, selectedGroupId: null };
      });
    } else if (range) {
      set((s) => {
        const anchorId = s.selectedLayerId;
        if (!anchorId) return { selectedLayerId: layerId, selectedLayerIds: [layerId], selectedGroupId: null };
        const ids = s.layers.map((l) => l.id);
        const ai = ids.indexOf(anchorId);
        const bi = ids.indexOf(layerId);
        const [lo, hi] = ai < bi ? [ai, bi] : [bi, ai];
        const rangeIds = ids.slice(lo, hi + 1);
        return { selectedLayerId: layerId, selectedLayerIds: rangeIds, selectedGroupId: null };
      });
    } else {
      set({ selectedLayerId: layerId, selectedLayerIds: [layerId], selectedGroupId: null });
    }
  },

  selectLayers: (layerIds) => {
    set({ selectedLayerId: layerIds[layerIds.length - 1] ?? null, selectedLayerIds: layerIds, selectedGroupId: null });
  },

  selectGroup: (groupId) => set({ selectedGroupId: groupId, selectedLayerId: null, selectedLayerIds: [] }),

  selectGroupLayers: (groupId) => {
    const groupLayers = get().layers.filter((l) => l.groupId === groupId);
    set({
      selectedLayerId: groupLayers[0]?.id ?? null,
      selectedLayerIds: groupLayers.map((l) => l.id),
      selectedGroupId: null,
    });
  },

  clearMultiSelection: () => {
    set((s) => ({ selectedLayerIds: s.selectedLayerId ? [s.selectedLayerId] : [] }));
  },

  renameLayer: (layerId, name) => {
    set((s) => ({
      layers: s.layers.map((l) => (l.id === layerId ? { ...l, name } : l)),
      isDirty: true,
    }));
  },

  setLayerColor: (layerId, color) => {
    set((s) => ({
      layers: s.layers.map((l) => (l.id === layerId ? { ...l, labelColor: color } : l)),
      isDirty: true,
    }));
  },

  duplicateLayer: (layerId) => {
    const { layers, pushUndo } = get();
    const src = layers.find((l) => l.id === layerId);
    if (!src) return;
    pushUndo();
    const clone: typeof src = {
      ...src,
      id: nanoid(12),
      name: `${src.name} (copy)`,
      // Slightly offset so the duplicate is visibly separate on canvas
      transform: { ...src.transform, x: src.transform.x + 20, y: src.transform.y + 20 },
    };
    const idx = layers.indexOf(src);
    const next = [...layers];
    next.splice(idx + 1, 0, clone);
    set({ layers: next, selectedLayerId: clone.id, isDirty: true });
  },

  addLayerGroup: () => {
    const newGroup: LayerGroup = {
      id: nanoid(12),
      name: 'Group',
      collapsed: false,
      sortOrder: get().layerGroups.length,
    };
    set((s) => ({ layerGroups: [...s.layerGroups, newGroup], isDirty: true }));
    return newGroup;
  },

  removeLayerGroup: (groupId) => {
    set((s) => ({
      // Ungroup all layers in this group
      layers: s.layers.map((l) => (l.groupId === groupId ? { ...l, groupId: null } : l)),
      layerGroups: s.layerGroups.filter((g) => g.id !== groupId),
      isDirty: true,
    }));
  },

  renameLayerGroup: (groupId, name) => {
    set((s) => ({
      layerGroups: s.layerGroups.map((g) => (g.id === groupId ? { ...g, name } : g)),
      isDirty: true,
    }));
  },

  toggleGroupCollapsed: (groupId) => {
    set((s) => ({
      layerGroups: s.layerGroups.map((g) => (g.id === groupId ? { ...g, collapsed: !g.collapsed } : g)),
      isDirty: true,
    }));
  },

  setLayerGroup: (layerId, groupId) => {
    set((s) => ({
      layers: s.layers.map((l) => (l.id === layerId ? { ...l, groupId } : l)),
      isDirty: true,
    }));
  },

  reorderGroups: (fromId, toId) => {
    set((s) => {
      const sorted = [...s.layerGroups].sort((a, b) => a.sortOrder - b.sortOrder);
      const fromIdx = sorted.findIndex((g) => g.id === fromId);
      const toIdx = sorted.findIndex((g) => g.id === toId);
      if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return {};
      const reordered = [...sorted];
      const [moved] = reordered.splice(fromIdx, 1);
      reordered.splice(toIdx, 0, moved);
      return {
        layerGroups: reordered.map((g, i) => ({ ...g, sortOrder: i })),
        isDirty: true,
      };
    });
  },

  fetchAnnotations: async () => {
    const { sceneId } = get();
    if (!sceneId) return;
    try {
      const res = await api.get<{ annotations: Annotation[] }>(`/api/scenes/${sceneId}/annotations`);
      if (Array.isArray(res.annotations)) set({ annotations: res.annotations });
    } catch { /* non-critical */ }
  },

  addAnnotation: async (x, y, text) => {
    const { sceneId } = get();
    if (!sceneId) return;
    const { useAuthStore } = await import('./authStore');
    const userId = useAuthStore.getState().user?.id ?? 'unknown';
    const res = await api.post<{ annotation: Annotation }>(`/api/scenes/${sceneId}/annotations`, { x, y, text });
    if (res.annotation) {
      set((s) => ({ annotations: [...s.annotations, res.annotation] }));
    }
    void userId;
  },

  updateAnnotation: async (id, text) => {
    const { sceneId } = get();
    if (!sceneId) return;
    await api.patch(`/api/scenes/${sceneId}/annotations/${id}`, { text });
    set((s) => ({
      annotations: s.annotations.map((a) =>
        a.id === id ? { ...a, text, updatedAt: new Date().toISOString() } : a
      ),
    }));
  },

  resolveAnnotation: (id, resolved) => {
    set((s) => ({
      annotations: s.annotations.map((a) => a.id === id ? { ...a, resolved } : a),
    }));
  },

  deleteAnnotation: async (id) => {
    const { sceneId } = get();
    if (!sceneId) return;
    await api.delete(`/api/scenes/${sceneId}/annotations/${id}`);
    set((s) => ({ annotations: s.annotations.filter((a) => a.id !== id) }));
  },

  pushUndo: (label = 'Edit') => {
    set((s) => {
      const snapshot = JSON.parse(JSON.stringify(s.layers)) as LayerSnapshot;
      const stack = [snapshot, ...s.undoStack].slice(0, UNDO_STACK_SIZE);
      const labels = [label, ...s.undoLabels].slice(0, UNDO_STACK_SIZE);
      return { undoStack: stack, redoStack: [], undoLabels: labels };
    });
  },

  undo: () => {
    set((s) => {
      if (s.undoStack.length === 0) return s;
      const [prev, ...rest] = s.undoStack;
      const [, ...restLabels] = s.undoLabels;
      const currentSnapshot = JSON.parse(JSON.stringify(s.layers)) as LayerSnapshot;
      return {
        layers: prev,
        undoStack: rest,
        undoLabels: restLabels,
        redoStack: [currentSnapshot, ...s.redoStack].slice(0, UNDO_STACK_SIZE),
        isDirty: true,
      };
    });
  },

  redo: () => {
    set((s) => {
      if (s.redoStack.length === 0) return s;
      const [next, ...rest] = s.redoStack;
      const currentSnapshot = JSON.parse(JSON.stringify(s.layers)) as LayerSnapshot;
      return {
        layers: next,
        redoStack: rest,
        undoStack: [currentSnapshot, ...s.undoStack].slice(0, UNDO_STACK_SIZE),
        undoLabels: ['Edit', ...s.undoLabels].slice(0, UNDO_STACK_SIZE),
        isDirty: true,
      };
    });
  },

  undoTo: (index: number) => {
    // Jump to a specific undo stack entry by calling undo() `index + 1` times
    for (let i = 0; i <= index; i++) {
      useSceneStore.getState().undo();
    }
  },

  setTrackTimeScale: (layerId, trackIndex, timeScale) => {
    set((s) => ({
      layers: s.layers.map((l) =>
        l.id !== layerId ? l : {
          ...l,
          animation: {
            ...l.animation,
            tracks: l.animation.tracks.map((t) =>
              t.trackIndex === trackIndex ? { ...t, timeScale } : t
            ),
          },
        }
      ),
      isDirty: true,
    }));
  },

  setCanvasSize: (w, h) => set({ canvasWidth: w, canvasHeight: h, isDirty: true }),
  setSceneName: (name) => set({ sceneName: name, isDirty: true }),
  setSceneNotes: (notes) => set({ sceneNotes: notes, isDirty: true }),

  save: async () => {
    const { sceneId, sceneName, toSceneData, lastSavedVersion } = get();
    if (!sceneId) return;
    await api.put(`/api/scenes/${sceneId}`, { sceneData: toSceneData(), name: sceneName });
    set({ isDirty: false, lastSavedVersion: lastSavedVersion + 1 });

    // Capture thumbnail in background — non-critical, failure is silently ignored
    (async () => {
      try {
        const { getSpineManager } = await import('../lib/spine-manager');
        const blob = await getSpineManager().captureScreenshot();
        const { useAuthStore } = await import('./authStore');
        const { token } = useAuthStore.getState();
        const BASE_URL = import.meta.env.VITE_API_URL ?? '';
        await fetch(`${BASE_URL}/api/scenes/${sceneId}/thumbnail`, {
          method: 'POST',
          headers: {
            'Content-Type': 'image/png',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: blob,
        });
      } catch {
        // Thumbnail upload is best-effort; never block save
      }
    })();
  },

  selectedLayer: () => {
    const { layers, selectedLayerId } = get();
    return layers.find((l) => l.id === selectedLayerId) ?? null;
  },

  toSceneData: (): SceneData => {
    const { canvasWidth, canvasHeight, backgroundColor, layers, layerGroups, sceneNotes } = get();
    return {
      version: 1,
      canvas: { width: canvasWidth, height: canvasHeight, backgroundColor },
      viewport: { zoom: 1, panX: 0, panY: 0 },
      layers,
      layerGroups,
      ...(sceneNotes ? { notes: sceneNotes } : {}),
    };
  },
}); });
